import posthog from 'posthog-js';
import { MemberData, WorkspaceData } from '~/api';
import {
  AppContexts,
  getRegionFromOrigin,
  isDebugLocalStorageKeyOn,
  isDevelopmentMode,
  isEnvHostedContext,
  isInternalUser,
  isScalekitPlatformProductionEnvironment,
  isStagingMode,
  Region
} from '~/utils/commonUtils.ts';
import { globalStore } from '~/stores';

// const POSTHOG_API_HOST_DOMAIN = 'https://app.posthog.com';
const POSTHOG_PROXY_DOMAIN = 'https://ph.scalekit.com';

const isPosthogDisabled = () => {
  if (isDebugLocalStorageKeyOn()) {
    return false;
  }

  return (
    isDevelopmentMode() ||
    isStagingMode() ||
    (!isScalekitPlatformProductionEnvironment() &&
      (globalStore.appContext === AppContexts.HOSTED_AUTH_PAGES ||
        globalStore.appContext === AppContexts.CUSTOMER_PORTAL ||
        isEnvHostedContext()))
  );
};

function initialize() {
  if (isPosthogDisabled()) {
    return;
  }
  posthog.init(import.meta.env.VITE_POSTHOG_PUBLIC_TOKEN, {
    api_host: POSTHOG_PROXY_DOMAIN
  });
}

function identifyUser(memberData: MemberData) {
  // Capture user name and email in case of non-EU domains
  const isEuDomain = getRegionFromOrigin() === Region.EU;
  if (isInternalUser(memberData.email)) {
    posthog.opt_out_capturing();
  } else {
    posthog.identify(
      memberData.externalId,
      !isEuDomain
        ? {
            name: `${memberData.firstName} ${memberData.lastName}`.trim() || '',
            email: memberData.email
          }
        : undefined
    );
  }
}

function groupIdentify(workspaceData: WorkspaceData) {
  posthog.group('workspace', workspaceData.id, {
    name: workspaceData.displayName,
    workspace_id: workspaceData.id
  });
}

function capturePosthogCustomEvent(
  eventName: string,
  properties?: Record<string, any>
) {
  posthog.capture(eventName, properties);
}

function resetIdentity() {
  posthog.reset();
}

function registerPosthogProperty(property: Record<string, any>) {
  posthog.register({
    ...property
  });
}

function getInitializer() {
  let isInitialized = false;
  return () => {
    if (isInitialized) {
      return;
    }
    initialize();
    isInitialized = true;
  };
}

const initPosthog = getInitializer();

export {
  initPosthog,
  identifyUser,
  groupIdentify,
  resetIdentity,
  registerPosthogProperty,
  capturePosthogCustomEvent
};
